import { memo, useMemo, useRef } from 'react';
import { useAnnotationDraft } from '@/features/comment-on-file';
import type { AnnotationTool } from '@/features/comment-on-file';
import type { MediaBox } from '../hooks/useMediaBox';
import { useShapeDrawing } from '../hooks/useShapeDrawing';
import { ArrowMarker } from '../shapes/ArrowShape';
import { arrowMarkerId } from '../shapes/ArrowShape.utils';
import { ShapeRenderer } from '../shapes/ShapeRenderer';
import type { RenderEntry } from '../hooks/usePersistedEntries';

const ENABLED_DRAWING_TOOLS = [
  'circle',
  'rectangle',
  'pen',
  'arrow',
] as const satisfies readonly AnnotationTool[];

interface Props {
  box: MediaBox | null;
  persisted: RenderEntry[];
}

export const ShapeOverlay = memo(function ShapeOverlay({ box, persisted }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { draftShapes, draftsVisible } = useAnnotationDraft();
  const drawing = useShapeDrawing({ svgRef, enabledTools: ENABLED_DRAWING_TOOLS });
  // Stabilize identity across renders so `arrowColors`'s useMemo dep array
  // doesn't invalidate every render (the conditional `[]` would be a fresh
  // reference every time without this).
  const renderedDrafts = useMemo(
    () => (draftsVisible ? draftShapes : []),
    [draftsVisible, draftShapes],
  );

  const arrowColors = useMemo(() => {
    const set = new Set<string>();
    for (const { shape, color } of persisted) {
      if (shape.type === 'arrow') set.add(color);
    }
    for (const draft of renderedDrafts) {
      if (draft.shape.type === 'arrow') set.add(draft.color);
    }
    return Array.from(set);
  }, [persisted, renderedDrafts]);

  if (!box) return null;

  return (
    <svg
      ref={svgRef}
      className="absolute"
      style={{
        left: box.left,
        top: box.top,
        width: box.width,
        height: box.height,
        pointerEvents: drawing.isActive ? 'auto' : 'none',
        cursor: drawing.isActive ? 'crosshair' : undefined,
        touchAction: drawing.isActive ? 'none' : undefined,
      }}
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      onPointerDown={drawing.onPointerDown}
      onPointerMove={drawing.onPointerMove}
      onPointerUp={drawing.onPointerUp}
      onPointerCancel={drawing.onPointerCancel}
    >
      {arrowColors.length > 0 && (
        <defs>
          {arrowColors.map((c) => (
            <ArrowMarker key={c} id={arrowMarkerId(c)} color={c} />
          ))}
        </defs>
      )}
      <g data-layer="persisted">
        {persisted.map(({ commentId, shape, color }, i) => (
          <ShapeRenderer key={`${commentId}-${i}`} shape={shape} color={color} boxSize={box} />
        ))}
      </g>
      {renderedDrafts.length > 0 && (
        <g data-layer="draft" style={{ pointerEvents: 'none' }}>
          {renderedDrafts.map((draft, i) => (
            <ShapeRenderer key={i} shape={draft.shape} color={draft.color} boxSize={box} />
          ))}
        </g>
      )}
    </svg>
  );
});
